import { useState, useEffect, useMemo, useCallback, useRef, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import { Receipt } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import { alpha, Theme } from "@mui/material/styles";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AddLinkIcon from "@mui/icons-material/AddLink";
import InputAdornment from "@mui/material/InputAdornment";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import CustomDatePicker from "../../ui/customDatePicker/customDatePicker.component";
import CustomModal from "../../ui/modal/modal.component";
import EquipmentCounterForm from "../../ui/equipmentCounterForm/equipmentCounterForm.component";
import {
  CAN_ENTER_EVERY_COUNTER_VALUE_PERMISSION,
  LowCounterValueConfirmModal,
  LowCounterValueWarning,
  parseCounterNumericValue,
} from "../../ui/equipmentCounterForm/lowCounterValueWarning.component";
import InboxIcon from "@mui/icons-material/Inbox";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import axios from "../../../api/axios";
import { useAnalytics } from "../../../api/analytics";
import { AnalyticsData } from "../../../types/analytics";
import { useHasPermission } from "../../../contexts/auth";
import {
  SETTLEMENT_STATUS_TO_COLOR,
  SETTLEMENTS_ENDPOINT,
  SETTLEMENTS_PENDING_ACTIONS_ENDPOINT,
  SUCCESS_SNACKBAR,
  ERROR_SNACKBAR,
} from "../../../consts";
import CustomCircularProgress from "../../ui/customCircularProgress/customCircularProgress.component";
import { SettlementPending, SettlementPendingPosition } from "../../../types/settlements";
import {
  formatCounterInputValue,
  formatCounterProvider,
  formatDateTime,
  normalizeCounterInputValue,
} from "../../../utils";
import {
  contractTermHasScanPrice,
  findContractTermPriceId,
  getEquipmentIdForPendingPosition,
  getSettlementClientName,
  getSettlementContractLabel,
  getSettlementContractNumber,
  getSettlementEquipments,
  isCommonCounterSettlement,
  renderEquipmentFullName,
  renderEquipmentFullNameList,
} from "../../../utils/settlementContractTerms";
import { createAndAssignSettlementCounter } from "../../../utils/settlementCounterActions";
import { compareByCounterKind } from "../../../utils/counterKindOrder";
import { CounterDetailsTable } from "./counterDetailsTable.component";
import styles from "./settlementCreator.module.css";

interface SettlementCreatorProps {
  contractId?: string;
  clientName?: string;
  onSuccess?: () => void;
}

const PENDING_SETTLEMENTS_PAGE_SIZE = 10;
const SETTLEMENT_STATUS_CONFIRMED = "C";

// Small colored circular badge behind the "newly attached"/"term ended"
// equipment icons - plain tinted icons alone read as too faint at a glance.
const statusIconBadgeSx = (color: "success" | "warning") => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  height: 26,
  borderRadius: "50%",
  color: `${color}.main`,
  bgcolor: (theme: Theme) => alpha(theme.palette[color].main, 0.15),
});

// Small tinted pill behind the contract/client "filter by this" search
// icons - a bare primary-colored icon at this size barely reads as
// different from the muted label text next to it.
const filterSearchIconButtonSx = {
  color: "primary.main",
  bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12),
  borderRadius: 1,
  "&:hover": {
    bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.24),
  },
};

// A counter input row - either a real pending position needing a value, or
// an ad-hoc one added on demand (currently only "Add Scan counter").
// SettlementPendingPosition already satisfies this shape structurally, so
// real positions can be passed anywhere a CounterActionRow is expected.
interface CounterActionRow {
  id: string;
  price?: string | number;
  counter_kind: string;
  counter_kind_display: string;
  equipment_id?: string | number | null;
  previous_counter_value?: number | null;
  previous_counter_read_at?: string | null;
  removable?: boolean;
}

export const SettlementCreator = ({ contractId, clientName, onSuccess }: SettlementCreatorProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { getAnalytics } = useAnalytics();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const canEnterEveryCounterValue = useHasPermission(CAN_ENTER_EVERY_COUNTER_VALUE_PERMISSION);
  const [lowValueConfirmOpen, setLowValueConfirmOpen] = useState<boolean>(false);
  const [lowValueWarnings, setLowValueWarnings] = useState<LowCounterValueWarning[]>([]);
  const pendingBulkSettlementRef = useRef<SettlementPending | null>(null);
  const [editingCounterId, setEditingCounterId] = useState<string | null>(null);
  const [pendingSettlements, setPendingSettlements] = useState<SettlementPending[]>([]);
  const [loadingPending, setLoadingPending] = useState<boolean>(false);
  const [loadingPendingMore, setLoadingPendingMore] = useState<boolean>(false);
  const [hasMorePending, setHasMorePending] = useState<boolean>(false);
  const [loadMorePendingFailed, setLoadMorePendingFailed] = useState<boolean>(false);
  const [pendingSearchInput, setPendingSearchInput] = useState<string>(clientName ?? "");
  const [pendingSearch, setPendingSearch] = useState<string>(clientName ?? "");
  const [pendingContractNumberSort, setPendingContractNumberSort] = useState<"asc" | "desc">("desc");
  const [counterValues, setCounterValues] = useState<Record<string, string>>({});
  const [bulkCounterSubmitting, setBulkCounterSubmitting] = useState<Record<string, boolean>>(
    {}
  );
  const [confirmValues, setConfirmValues] = useState<
    Record<string, { saleDate: string; totalOverridden: string }>
  >({});
  const [confirmSubmitting, setConfirmSubmitting] = useState<Record<string, boolean>>(
    {}
  );
  const [scanAdHocRows, setScanAdHocRows] = useState<Record<string, CounterActionRow[]>>({});
  const scanAdHocIdRef = useRef(0);
  const pendingScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);
  // Bumped by every reload of the list (page 1 - a new search or sort, a
  // refresh after an action), and a response to an older request is dropped:
  // a slower answer for an earlier search, or the next page of the previous
  // results, would otherwise leave the paging state describing another list
  // than the one shown, and scrolling would keep asking for a page past its end.
  const pendingListVersionRef = useRef(0);
  // The page scrolling loads next - null while there is none to ask for: no
  // more pages, one already on its way, or the list reloading (an observer set
  // up for the previous search would otherwise page through the new one).
  const pendingNextPageRef = useRef<number | null>(null);

  const selectedContractId = contractId ?? null;

  const defaultSaleDate = useMemo(
    () => dayjs().format("YYYY-MM-DD"),
    []
  );

  // Clicking the search icon next to a card's contract number / client name
  // drops that value into the list search box (or clears it if it's already
  // the active query), then scrolls the list back to the top.
  const applyPendingSearchValue = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "-") {
      return;
    }
    setPendingSearchInput((current) => (current.trim() === trimmed ? "" : trimmed));
    pendingScrollContainerRef.current?.scrollTo({ top: 0 });
  }, []);

  const fetchPendingSettlements = useCallback(async (page = 1, append = false) => {
    if (!append) {
      pendingListVersionRef.current += 1;
      pendingNextPageRef.current = null;
    }
    const version = pendingListVersionRef.current;
    const isLatest = () => version === pendingListVersionRef.current;

    setLoadMorePendingFailed(false);
    if (append) {
      setLoadingPendingMore(true);
    } else {
      setLoadingPending(true);
    }

    try {
      const params = {
        ...(selectedContractId ? { contract_id: selectedContractId } : {}),
        ...(pendingSearch ? { search: pendingSearch } : {}),
        contract_number_sort: pendingContractNumberSort,
        page,
        page_size: PENDING_SETTLEMENTS_PAGE_SIZE,
      };
      const response = await axios.get(SETTLEMENTS_PENDING_ACTIONS_ENDPOINT, { params });
      const payload = response.data;
      const settlements = Array.isArray(payload) ? payload : payload?.results || [];
      const totalCount = Array.isArray(payload)
        ? settlements.length
        : Number(payload?.count ?? settlements.length);
      const hasNextPage = Array.isArray(payload) ? false : Boolean(payload?.next);

      if (isLatest()) {
        setPendingSettlements((prev) => (append ? [...prev, ...settlements] : settlements));
        setHasMorePending(hasNextPage);
        pendingNextPageRef.current = hasNextPage ? page + 1 : null;
      }

      return { settlements, totalCount, hasNextPage };
    } catch (error) {
      if (isLatest()) {
        // Never asked for again by itself: the trigger is still in view, so
        // the observer would retry at once and keep failing - and every
        // attempt counts towards the rate limit. A failed next page waits
        // for "Retry" instead.
        setHasMorePending(false);
        if (append) {
          pendingNextPageRef.current = page;
          setLoadMorePendingFailed(true);
        } else {
          enqueueSnackbar(t("Failed to fetch settlements"), ERROR_SNACKBAR);
        }
      }
    } finally {
      // Both flags: a reload also ends the "load more" it overtook, whose
      // own response is dropped.
      if (isLatest()) {
        setLoadingPending(false);
        setLoadingPendingMore(false);
      }
    }
    return { settlements: [], totalCount: 0, hasNextPage: false };
  }, [selectedContractId, pendingSearch, pendingContractNumberSort, enqueueSnackbar, t]);

  const loadMorePendingSettlements = useCallback(() => {
    const page = pendingNextPageRef.current;
    if (page === null) {
      return;
    }
    pendingNextPageRef.current = null;
    fetchPendingSettlements(page, true);
  }, [fetchPendingSettlements]);

  useEffect(() => {
    fetchPendingSettlements(1, false);
  }, [fetchPendingSettlements]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPendingSearch(pendingSearchInput.trim());
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [pendingSearchInput]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchPendingSettlements(1, false);
    };

    window.addEventListener("settlementAdded", handleRefresh);
    window.addEventListener("settlementUpdated", handleRefresh);
    window.addEventListener("equipmentCounterAdded", handleRefresh);

    return () => {
      window.removeEventListener("settlementAdded", handleRefresh);
      window.removeEventListener("settlementUpdated", handleRefresh);
      window.removeEventListener("equipmentCounterAdded", handleRefresh);
    };
  }, [fetchPendingSettlements]);

  const fetchAnalytics = useCallback(async () => {
    const data = await getAnalytics();
    if (data) {
      setAnalyticsData(data);
    }
  }, [getAnalytics]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    window.addEventListener("settlementAdded", fetchAnalytics);
    window.addEventListener("settlementUpdated", fetchAnalytics);
    window.addEventListener("settlementConfirmed", fetchAnalytics);

    return () => {
      window.removeEventListener("settlementAdded", fetchAnalytics);
      window.removeEventListener("settlementUpdated", fetchAnalytics);
      window.removeEventListener("settlementConfirmed", fetchAnalytics);
    };
  }, [fetchAnalytics]);

  useEffect(() => {
    if (!hasMorePending || loadingPending || loadingPendingMore) {
      return;
    }

    const trigger = loadMoreTriggerRef.current;
    if (!trigger) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry?.isIntersecting && !loadingPendingMore && !loadingPending) {
          loadMorePendingSettlements();
        }
      },
      {
        root: pendingScrollContainerRef.current,
        rootMargin: "200px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(trigger);

    return () => {
      observer.disconnect();
    };
  }, [
    hasMorePending,
    loadingPending,
    loadingPendingMore,
    loadMorePendingSettlements,
  ]);

  const getMissingCounterPositions = (positions: SettlementPendingPosition[] = []) =>
    positions.filter((position) => !position.counter_id);

  const getOutdatedCounterPositions = (positions: SettlementPendingPosition[] = []) =>
    positions.filter(
      (position) =>
        position.counter_id &&
        (position.investigation_reasons || []).includes("O")
    );

  const hasInvestigationReasons = (positions: SettlementPendingPosition[] = []) =>
    positions.some((position) => (position.investigation_reasons || []).length > 0);

  const getDistinctInvestigationReasonLabels = (
    positions: SettlementPendingPosition[] = []
  ) => {
    const labels = positions
      .map((position) => position.investigation_reasons_display)
      .filter(Boolean) as string[];

    return Array.from(new Set(labels));
  };

  const canPrefillConfirm = (settlement: SettlementPending) => {
    const positions = settlement.positions || [];
    const hasCounterMissing = positions.some((position) => !position.counter_id);
    return settlement.status === "W" && !hasCounterMissing && !hasInvestigationReasons(positions);
  };

  const getPreviousCounterData = (positions: SettlementPendingPosition[] = []) => {
    return positions
      .filter((position) =>
        position.previous_counter_value !== null &&
        position.previous_counter_value !== undefined
      )
      .sort((a, b) => compareByCounterKind(a.counter_kind, b.counter_kind))
      .map((position) => ({
        id: position.counter_id ?? undefined,
        kind: position.counter_kind_display,
        value: position.counter_value as string | number,
        previousValue: position.previous_counter_value as string | number,
        difference: position.counter_difference as string | number,
        previousReadDate: position.previous_counter_read_at
          ? formatDateTime(position.previous_counter_read_at)
          : "-",
        currentReadDate: position.counter_read_at ? formatDateTime(position.counter_read_at) : "-",
        previousProvider: formatCounterProvider(
          position.previous_counter_provider_display,
          position.previous_counter_email_source_display,
        ),
        previousProviderCode: position.previous_counter_provider || "-",
        currentProvider: formatCounterProvider(
          position.counter_provider_display,
          position.counter_email_source_display,
        ),
        currentProviderCode: position.counter_provider || "-",
      }));
  };

  const getCounterKey = (settlementId: string, positionId?: string) =>
    `${settlementId}-${positionId || "unknown"}`;

  interface EquipmentPositionGroup {
    equipmentId: string;
    equipmentName: ReactNode;
    positions: SettlementPendingPosition[];
  }

  const groupPositionsByEquipment = (
    settlement: SettlementPending,
    positions: SettlementPendingPosition[] = []
  ): EquipmentPositionGroup[] => {
    const groups = new Map<string, EquipmentPositionGroup>();

    // Seed a group per equipment on the term even before any position exists
    // for it, so equipment with nothing outstanding can still offer an
    // "Add Scan counter" button.
    for (const equipment of settlement.contract_term?.equipments || []) {
      const equipmentId = String(equipment.id);
      groups.set(equipmentId, {
        equipmentId,
        equipmentName: renderEquipmentFullName(equipment, equipment.serial || equipmentId),
        positions: [],
      });
    }

    for (const position of positions) {
      const equipmentId =
        position.equipment_id != null ? String(position.equipment_id) : "unknown";
      const existing = groups.get(equipmentId);

      if (existing) {
        existing.positions.push(position);
        continue;
      }

      groups.set(equipmentId, {
        equipmentId,
        equipmentName: position.equipment_full_name || equipmentId,
        positions: [position],
      });
    }

    return Array.from(groups.values());
  };

  const getSettlementTitle = (settlement: SettlementPending): ReactNode => {
    if (isCommonCounterSettlement(settlement)) {
      return t("Common counter for {{count}} equipments", {
        count: getSettlementEquipments(settlement).length,
      });
    }
    return renderEquipmentFullNameList(settlement.contract_term?.equipments, t("Settlement"));
  };

  // Confirmed settlements only surface here as search results (see
  // fetchPendingSettlements/pending-actions) - the backend already scopes
  // them to the query, but this re-checks the same fields shown on the card
  // (equipment serial, contract number/name, client name) so a settlement
  // never appears without something visibly matching what was typed.
  const confirmedSettlementMatchesQuery = (settlement: SettlementPending, query: string) => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return false;
    }

    const haystacks = [
      settlement.contract_term?.contract_info?.number,
      settlement.contract_term?.contract_info?.internal_name,
      settlement.contract_term?.client_name,
      ...(settlement.contract_term?.equipments || []).map((equipment) => equipment.serial),
    ];

    return haystacks.some((haystack) =>
      (haystack || "").toLowerCase().includes(normalizedQuery)
    );
  };

  const renderCounterInput = (
    settlement: SettlementPending,
    position: CounterActionRow
  ) => {
    const key = getCounterKey(settlement.id, position.id);

    return (
      <Stack
        key={`${settlement.id}-${position.id}-input`}
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ width: { xs: "100%", sm: "auto" } }}
      >
        <TextField
          label={position.counter_kind_display || t("Counter value")}
          size="small"
          value={formatCounterInputValue(counterValues[key] || "")}
          onChange={(event) => {
            handleCounterValueChange(settlement.id, position.id, event.target.value);
          }}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          sx={{ width: { xs: "100%", sm: 220 } }}
        />
        {position.removable && (
          <IconButton
            size="small"
            color="inherit"
            onClick={() => removeScanAdHocRow(settlement.id, position.id)}
            aria-label={t("Remove")}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    );
  };

  const handleCounterValueChange = (
    settlementId: string,
    positionId: string | undefined,
    value: string
  ) => {
    const key = getCounterKey(settlementId, positionId);
    const normalizedValue = normalizeCounterInputValue(value);
    if (normalizedValue === null) {
      return;
    }
    setCounterValues((prev) => ({ ...prev, [key]: normalizedValue }));
  };

  const handleConfirmValueChange = (
    settlementId: string,
    field: "saleDate" | "totalOverridden",
    value: string
  ) => {
    setConfirmValues((prev) => ({
      ...prev,
      [settlementId]: {
        saleDate: prev[settlementId]?.saleDate || defaultSaleDate,
        totalOverridden: prev[settlementId]?.totalOverridden || "",
        [field]: value,
      },
    }));
  };

  const addScanRow = (settlement: SettlementPending, equipmentId: string | number) => {
    scanAdHocIdRef.current += 1;
    const row: CounterActionRow = {
      id: `scan-${settlement.id}-${equipmentId}-${scanAdHocIdRef.current}`,
      price: findContractTermPriceId(settlement.contract_term, equipmentId, "SP"),
      counter_kind: "SP",
      counter_kind_display: t("Scanned pages"),
      equipment_id: equipmentId,
      removable: true,
    };
    setScanAdHocRows((prev) => ({
      ...prev,
      [settlement.id]: [...(prev[settlement.id] || []), row],
    }));
  };

  const removeScanAdHocRow = (settlementId: string, rowId: string) => {
    setScanAdHocRows((prev) => ({
      ...prev,
      [settlementId]: (prev[settlementId] || []).filter((row) => row.id !== rowId),
    }));
    setCounterValues((prev) => {
      const next = { ...prev };
      delete next[getCounterKey(settlementId, rowId)];
      return next;
    });
  };

  const getBulkCounterRowsToSubmit = (settlement: SettlementPending) => {
    const positions = settlement.positions || [];
    const actionPositions: CounterActionRow[] = [
      ...getMissingCounterPositions(positions),
      ...getOutdatedCounterPositions(positions),
      ...(scanAdHocRows[settlement.id] || []),
    ];

    return actionPositions
      .map((position) => ({
        position,
        key: getCounterKey(settlement.id, position.id),
        equipmentId:
          position.equipment_id != null
            ? String(position.equipment_id)
            : getEquipmentIdForPendingPosition(
                settlement,
                positions.indexOf(position as SettlementPendingPosition)
              ),
      }))
      .filter(({ key }) => (counterValues[key] || "").trim() !== "");
  };

  const performBulkAddCounterValues = async (
    settlement: SettlementPending,
    rowsToSubmit: ReturnType<typeof getBulkCounterRowsToSubmit>
  ) => {
    setBulkCounterSubmitting((prev) => ({ ...prev, [settlement.id]: true }));
    try {
      for (const { position, key, equipmentId } of rowsToSubmit) {
        if (!equipmentId) {
          continue;
        }
        // Pinned to its price (when known) so this explicit, manually
        // entered value resolves the investigation regardless of the
        // configured counter priority provider - see
        // createAndAssignSettlementCounter.
        await createAndAssignSettlementCounter({
          settlementId: settlement.id,
          equipmentId,
          kind: position.counter_kind,
          value: counterValues[key],
          priceId: position.price,
        });
      }
      await axios.post(`${SETTLEMENTS_ENDPOINT}${settlement.id}/refresh-calculation/`);
      enqueueSnackbar(t("Counter values saved"), SUCCESS_SNACKBAR);
      setCounterValues((prev) => {
        const next = { ...prev };
        rowsToSubmit.forEach(({ key }) => delete next[key]);
        return next;
      });
      setScanAdHocRows((prev) => ({
        ...prev,
        [settlement.id]: (prev[settlement.id] || []).filter(
          (row) => !rowsToSubmit.some(({ position }) => position.id === row.id)
        ),
      }));
      window.dispatchEvent(new Event("equipmentCounterAdded"));
    } catch (error) {
      const message = (error as any)?.response?.data?.message || t("Failed to add counter value");
      enqueueSnackbar(message, ERROR_SNACKBAR);
    } finally {
      setBulkCounterSubmitting((prev) => ({ ...prev, [settlement.id]: false }));
    }
  };

  const requestBulkAddCounterValues = (settlement: SettlementPending) => {
    const rowsToSubmit = getBulkCounterRowsToSubmit(settlement);

    if (rowsToSubmit.length === 0) {
      enqueueSnackbar(t("Please enter at least one counter value"), ERROR_SNACKBAR);
      return;
    }

    if (rowsToSubmit.some(({ position, equipmentId }) => !equipmentId || !position.counter_kind)) {
      enqueueSnackbar(t("Equipment is required to add counter"), ERROR_SNACKBAR);
      return;
    }

    if (canEnterEveryCounterValue) {
      const warnings: LowCounterValueWarning[] = rowsToSubmit
        .map(({ position, key }): LowCounterValueWarning | null => {
          const enteredValue = parseCounterNumericValue(counterValues[key]);
          const latestValue = parseCounterNumericValue(position.previous_counter_value);
          if (enteredValue === null || latestValue === null || enteredValue >= latestValue) {
            return null;
          }
          return {
            key,
            label: position.counter_kind_display,
            enteredValue,
            latestValue,
            latestDate: position.previous_counter_read_at
              ? formatDateTime(position.previous_counter_read_at)
              : undefined,
          };
        })
        .filter((warning): warning is LowCounterValueWarning => warning !== null);

      if (warnings.length > 0) {
        setLowValueWarnings(warnings);
        pendingBulkSettlementRef.current = settlement;
        setLowValueConfirmOpen(true);
        return;
      }
    }

    void performBulkAddCounterValues(settlement, rowsToSubmit);
  };

  const handleConfirmLowValueBulkAdd = () => {
    const settlement = pendingBulkSettlementRef.current;
    pendingBulkSettlementRef.current = null;
    if (!settlement) {
      return;
    }
    void performBulkAddCounterValues(settlement, getBulkCounterRowsToSubmit(settlement));
  };

  const handleConfirmSettlement = async (settlement: SettlementPending) => {
    const values = confirmValues[settlement.id] || {
      saleDate: defaultSaleDate,
      totalOverridden: "",
    };

    if (!values.saleDate) {
      enqueueSnackbar(t("Sale date is required"), ERROR_SNACKBAR);
      return;
    }

    setConfirmSubmitting((prev) => ({ ...prev, [settlement.id]: true }));
    try {
      await axios.patch(`${SETTLEMENTS_ENDPOINT}${settlement.id}/`, {
        status: "C",
        sale_date: values.saleDate,
        total_overridden: values.totalOverridden || null,
      });
      enqueueSnackbar(t("Settlement confirmed"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("settlementUpdated"));
      window.dispatchEvent(new Event("settlementConfirmed"));
      const remaining = await fetchPendingSettlements(1, false);
      // Only jump to the contract's sale summary when this creator was opened
      // scoped to a single contract with no extra text filter applied - a
      // client-scoped or globally-filtered list emptying out just means the
      // filter matched nothing left, not that all work is done, so stay put
      // and let the confirmed settlement simply disappear from the list.
      if (remaining.totalCount === 0 && selectedContractId && !pendingSearch) {
        onSuccess?.();
        navigate(`/contracts/${selectedContractId}/sale-summary/${values.saleDate}`);
      }
    } catch (error) {
      // The backend's reason when it gives one (e.g. too many requests), so
      // the user - and whoever they report it to - can tell what happened.
      const message = (error as any)?.response?.data?.message || t("Failed to confirm settlement");
      enqueueSnackbar(message, ERROR_SNACKBAR);
    } finally {
      setConfirmSubmitting((prev) => ({ ...prev, [settlement.id]: false }));
    }
  };

  const handleDeleteSettlement = async (settlementId: string) => {
    try {
      await axios.delete(`${SETTLEMENTS_ENDPOINT}${settlementId}/`);
      enqueueSnackbar(t("Settlement deleted"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("settlementUpdated"));
      await fetchPendingSettlements(1, false);
    } catch (error) {
      const message = (error as any)?.response?.data?.message || t("Failed to delete settlement");
      enqueueSnackbar(message, ERROR_SNACKBAR);
    }
  };

  const allCurrentMonth = analyticsData?.settlements.all_current_month || 0;
  const readyToSettleCurrentMonth = analyticsData?.settlements.ready_to_settle_current_month || 0;
  const confirmedCurrentMonth = analyticsData?.settlements.confirmed_current_month || 0;
  const readyToSettleProgress =
    allCurrentMonth > 0 ? (readyToSettleCurrentMonth / allCurrentMonth) * 100 : 0;
  const readyToSettleProgressClamped = Math.max(0, Math.min(100, readyToSettleProgress));
  const settledProgress =
    readyToSettleCurrentMonth > 0 ? (confirmedCurrentMonth / readyToSettleCurrentMonth) * 100 : 0;
  const settledProgressClamped = Math.max(0, Math.min(100, settledProgress));

  return (
    <Box className={styles.mainContainer}>
      {analyticsData && (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ width: "100%" }}>
          <Paper
            variant="outlined"
            sx={{
              flex: "1 1 220px",
              minWidth: 200,
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: 2,
            }}
          >
            <Receipt sx={{ color: "#0288d1", fontSize: 20, opacity: 0.8 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {t("Ready to Settle / Current Month")}
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
                  {`${readyToSettleCurrentMonth}/${allCurrentMonth}`}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={readyToSettleProgressClamped}
                sx={{ height: 5, borderRadius: 999, mt: 0.5 }}
              />
            </Box>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              flex: "1 1 220px",
              minWidth: 200,
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: 2,
            }}
          >
            <Receipt sx={{ color: "#2e7d32", fontSize: 20, opacity: 0.8 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {t("Settled / Ready to Settle")}
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
                  {`${confirmedCurrentMonth}/${readyToSettleCurrentMonth}`}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={settledProgressClamped}
                sx={{ height: 5, borderRadius: 999, mt: 0.5 }}
              />
            </Box>
          </Paper>
        </Stack>
      )}
      <Box className={styles.tabsContainer}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            label={t("Search by machine serial, client, contract number")}
            size="small"
            value={pendingSearchInput}
            onChange={(event) => setPendingSearchInput(event.target.value)}
            fullWidth
            sx={{ flex: "1 1 220px", minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: pendingSearchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setPendingSearchInput("");
                      setPendingSearch("");
                    }}
                    aria-label={t("Clear")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={pendingContractNumberSort}
            onChange={(_event, value: "asc" | "desc" | null) => {
              if (value !== null) {
                setPendingContractNumberSort(value);
              }
            }}
            aria-label={t("Ordering") + ":"}
            sx={{
              flexWrap: "wrap",
              flex: { xs: "1 1 100%", sm: "1 1 220px" },
              minWidth: { xs: "100%", sm: 200 },
            }}
          >
            <ToggleButton value="desc" sx={{ flex: 1 }}>
              {t("Newest number first")}
            </ToggleButton>
            <ToggleButton value="asc" sx={{ flex: 1 }}>
              {t("Oldest number first")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {loadingPending && pendingSettlements.length === 0 ? (
          <CustomCircularProgress />
        ) : pendingSettlements.length === 0 ? (
          <Stack
            spacing={1}
            alignItems="center"
            justifyContent="center"
            className={styles.noSettlementsBox}
            sx={{
              padding: 4,
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "divider",
              color: "text.secondary",
            }}
          >
            <InboxIcon className={styles.noSettlementsIcon} />
            <Typography variant="subtitle1">{t("No pending settlements")}</Typography>
          </Stack>
        ) : (
          <Box ref={pendingScrollContainerRef} className={styles.pendingScrollContainer}>
          <Stack spacing={2}>
            {pendingSettlements.map((settlement) => {
              const isConfirmed = settlement.status === SETTLEMENT_STATUS_CONFIRMED;
              if (isConfirmed && !confirmedSettlementMatchesQuery(settlement, pendingSearch)) {
                // Confirmed settlements only ever belong here as search
                // results - the backend already scopes them to the query,
                // but skip rendering if this one doesn't visibly match (e.g.
                // the search was cleared out from under an in-flight request).
                return null;
              }
              const statusColor = SETTLEMENT_STATUS_TO_COLOR[settlement.status];

              const positions = settlement.positions || [];
              const missingPositions = getMissingCounterPositions(positions);
              const outdatedPositions = getOutdatedCounterPositions(positions);
              const settlementScanRows = scanAdHocRows[settlement.id] || [];
              const counterActionPositions: CounterActionRow[] = [
                ...missingPositions,
                ...outdatedPositions,
                ...settlementScanRows,
              ];
              const hasInvestigation = hasInvestigationReasons(positions);
              const readyToConfirm = canPrefillConfirm(settlement);
              const previousCounterData = getPreviousCounterData(positions);
              const distinctInvestigationReasonLabels =
                getDistinctInvestigationReasonLabels(positions);
              const isCommonCounter = isCommonCounterSettlement(settlement);
              const termEquipments = settlement.contract_term?.equipments || [];
              const singleEquipmentId =
                !isCommonCounter && termEquipments.length === 1
                  ? termEquipments[0].id
                  : undefined;
              const showScanButtonForSingle =
                !isCommonCounter &&
                singleEquipmentId != null &&
                !counterActionPositions.some((row) => row.counter_kind === "SP") &&
                contractTermHasScanPrice(settlement.contract_term, singleEquipmentId);
              const bulkSaveButton =
                counterActionPositions.length > 0 ? (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={bulkCounterSubmitting[settlement.id]}
                    onClick={() => requestBulkAddCounterValues(settlement)}
                  >
                    {bulkCounterSubmitting[settlement.id]
                      ? t("Processing...")
                      : t("Confirm counter values", { defaultValue: "Confirm" })}
                  </Button>
                ) : null;

              return (
                <Paper
                  key={settlement.id}
                  variant="outlined"
                  className={styles.settlementCard}
                  sx={
                    isConfirmed
                      ? { borderLeft: "4px solid", borderLeftColor: `${statusColor}.main` }
                      : undefined
                  }
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Stack spacing={0.5} flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle1">
                            {getSettlementTitle(settlement)}
                          </Typography>
                          {isConfirmed && (
                            <Chip label={t("Confirmed")} color={statusColor} size="small" />
                          )}
                          {settlement.contract_term?.contract_info?.is_brutto && (
                            <Chip
                              label={t("Gross (Brutto)")}
                              color="warning"
                              size="small"
                              title={t("This contract's values are stored and settled as gross (brutto) amounts")}
                            />
                          )}
                          {settlement.is_first_settlement && (
                            <Tooltip
                              title={t(
                                "This equipment was newly attached to this contract - first settlement for it here"
                              )}
                            >
                              <Box sx={statusIconBadgeSx("success")}>
                                <AddLinkIcon fontSize="small" />
                              </Box>
                            </Tooltip>
                          )}
                          {settlement.contract_term?.active === false && (
                            <Tooltip title={t("This equipment's term has already been ended on this contract")}>
                              <Box sx={statusIconBadgeSx("warning")}>
                                <LinkOffIcon fontSize="small" />
                              </Box>
                            </Tooltip>
                          )}
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              {t("Contract")}: {getSettlementContractLabel(settlement)}
                            </Typography>
                            {getSettlementContractNumber(settlement) !== "-" && (
                              <Tooltip title={t("Filter list by this contract")}>
                                <IconButton
                                  size="small"
                                  sx={{ p: 0.5, ...filterSearchIconButtonSx }}
                                  aria-label={t("Filter list by this contract")}
                                  onClick={() =>
                                    applyPendingSearchValue(getSettlementContractNumber(settlement))
                                  }
                                >
                                  <SearchIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              {t("Client")}: {getSettlementClientName(settlement)}
                            </Typography>
                            {getSettlementClientName(settlement) !== "-" && (
                              <Tooltip title={t("Filter list by this client")}>
                                <IconButton
                                  size="small"
                                  sx={{ p: 0.5, ...filterSearchIconButtonSx }}
                                  aria-label={t("Filter list by this client")}
                                  onClick={() =>
                                    applyPendingSearchValue(getSettlementClientName(settlement))
                                  }
                                >
                                  <SearchIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>
                      </Stack>
                      {!isConfirmed && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSettlement(settlement.id)}
                          sx={{ color: "text.secondary" }}
                        >
                          <DeleteIcon/>
                        </IconButton>
                      )}
                    </Stack>
                    {!isConfirmed && !isCommonCounter && previousCounterData.length > 0 && (
                        <Paper
                          variant="outlined"
                          className={styles.previousCounterSection}
                        >
                          <CounterDetailsTable
                            title={t("Previous counter")}
                            data={previousCounterData}
                            showEditButton
                            onEdit={(counterId) => setEditingCounterId(counterId)}
                          />
                        </Paper>
                      )}

                    {!isConfirmed && <Divider />}

                    {!isConfirmed && (
                    <Paper
                      variant="outlined"
                      className={styles.counterDetailsSection}
                    >
                      <Stack spacing={2}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          className={styles.counterActionStack}
                        >
                          <Typography variant="subtitle2">
                            {t("Actions needed:")}
                          </Typography>
                          {!isCommonCounter &&
                            hasInvestigation &&
                            distinctInvestigationReasonLabels.map((reasonLabel) => (
                                <Chip
                                  key={`${settlement.id}-${reasonLabel}`}
                                  label={reasonLabel}
                                  size="small"
                                />
                              ))}
                          {readyToConfirm && (
                            <Chip label={t("Ready to confirm")} size="small" />
                          )}
                        </Stack>

                        {isCommonCounter ? (
                          <Stack spacing={1.5}>
                            {groupPositionsByEquipment(settlement, positions).map((equipmentGroup) => {
                              const groupMissingPositions = getMissingCounterPositions(
                                equipmentGroup.positions
                              );
                              const groupOutdatedPositions = getOutdatedCounterPositions(
                                equipmentGroup.positions
                              );
                              const groupScanRows = settlementScanRows.filter(
                                (row) => String(row.equipment_id) === equipmentGroup.equipmentId
                              );
                              const groupCounterActionPositions: CounterActionRow[] = [
                                ...groupMissingPositions,
                                ...groupOutdatedPositions,
                                ...groupScanRows,
                              ];
                              const groupInvestigationLabels = getDistinctInvestigationReasonLabels(
                                equipmentGroup.positions
                              );
                              const groupPreviousCounterData = getPreviousCounterData(
                                equipmentGroup.positions
                              );
                              const groupNeedsScanButton =
                                !groupCounterActionPositions.some(
                                  (row) => row.counter_kind === "SP"
                                ) &&
                                contractTermHasScanPrice(
                                  settlement.contract_term,
                                  equipmentGroup.equipmentId
                                );
                              const hasGroupContent =
                                groupInvestigationLabels.length > 0 ||
                                groupPreviousCounterData.length > 0 ||
                                groupCounterActionPositions.length > 0 ||
                                groupNeedsScanButton;

                              if (!hasGroupContent) {
                                return null;
                              }

                              return (
                                <Stack
                                  key={`${settlement.id}-${equipmentGroup.equipmentId}`}
                                  spacing={1.5}
                                  sx={{
                                    p: 1.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {equipmentGroup.equipmentName}
                                  </Typography>

                                  {groupPreviousCounterData.length > 0 && (
                                    <Paper
                                      variant="outlined"
                                      className={styles.previousCounterSection}
                                    >
                                      <CounterDetailsTable
                                        title={t("Previous counter")}
                                        data={groupPreviousCounterData}
                                        showEditButton
                                        onEdit={(counterId) => setEditingCounterId(counterId)}
                                      />
                                    </Paper>
                                  )}

                                  {(groupPreviousCounterData.length > 0 &&
                                    (groupInvestigationLabels.length > 0 ||
                                      groupCounterActionPositions.length > 0)) && <Divider />}

                                  {groupInvestigationLabels.length > 0 && (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      flexWrap="wrap"
                                      className={styles.counterActionStack}
                                    >
                                      <Typography variant="subtitle2">
                                        {t("Actions needed:")}
                                      </Typography>
                                      {groupInvestigationLabels.map((reasonLabel) => (
                                        <Chip
                                          key={`${settlement.id}-${equipmentGroup.equipmentId}-${reasonLabel}`}
                                          label={reasonLabel}
                                          size="small"
                                        />
                                      ))}
                                    </Stack>
                                  )}

                                  {groupCounterActionPositions.length > 0 && (
                                    <Stack
                                      direction="row"
                                      spacing={2}
                                      flexWrap="wrap"
                                      alignItems="flex-start"
                                    >
                                      {groupCounterActionPositions.map((position) =>
                                        renderCounterInput(settlement, position)
                                      )}
                                    </Stack>
                                  )}

                                  {groupNeedsScanButton && (
                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={<AddIcon fontSize="small" />}
                                      onClick={() =>
                                        addScanRow(settlement, equipmentGroup.equipmentId)
                                      }
                                      sx={{ alignSelf: "flex-start" }}
                                    >
                                      {t("Add Scan counter")}
                                    </Button>
                                  )}
                                </Stack>
                              );
                            })}
                            {bulkSaveButton && (
                              <Stack direction="row" spacing={1} className={styles.counterActionStack}>
                                {bulkSaveButton}
                              </Stack>
                            )}
                          </Stack>
                        ) : (
                          <>
                            {counterActionPositions.length > 0 && (
                              <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
                                {counterActionPositions.map((position) =>
                                  renderCounterInput(settlement, position)
                                )}
                                {bulkSaveButton}
                              </Stack>
                            )}

                            {showScanButtonForSingle && singleEquipmentId != null && (
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<AddIcon fontSize="small" />}
                                onClick={() => addScanRow(settlement, singleEquipmentId)}
                                sx={{ alignSelf: "flex-start" }}
                              >
                                {t("Add Scan counter")}
                              </Button>
                            )}
                          </>
                        )}

                        {readyToConfirm && (
                          <Stack spacing={1}>
                            <Typography variant="subtitle2">
                              {t("Confirm settlement")}
                            </Typography>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ xs: "stretch", sm: "center" }}
                            >
                              <CustomDatePicker
                                label={t("Sale date")}
                                value={dayjs(
                                  confirmValues[settlement.id]?.saleDate || defaultSaleDate
                                )}
                                onChange={(value: Dayjs | null) =>
                                  handleConfirmValueChange(
                                    settlement.id,
                                    "saleDate",
                                    value ? value.format("YYYY-MM-DD") : ""
                                  )
                                }
                                slotProps={{
                                  textField: {
                                    size: "small",
                                  },
                                }}
                              />
                              <TextField
                                size="small"
                                label={t("Total Overridden")}
                                value={confirmValues[settlement.id]?.totalOverridden || ""}
                                onChange={(event) =>
                                  handleConfirmValueChange(
                                    settlement.id,
                                    "totalOverridden",
                                    event.target.value
                                  )
                                }
                              />
                              <Button
                                variant="contained"
                                size="small"
                                disabled={confirmSubmitting[settlement.id]}
                                onClick={() => handleConfirmSettlement(settlement)}
                              >
                                {confirmSubmitting[settlement.id]
                                  ? t("Processing...")
                                  : t("Confirm")}
                              </Button>
                            </Stack>
                          </Stack>
                        )}
                      </Stack>
                    </Paper>
                    )}
                  </Stack>
                </Paper>
              );
            })}

            {hasMorePending && <Box ref={loadMoreTriggerRef} sx={{ height: 1 }} />}
            {loadingPendingMore && (
              <Stack alignItems="center" py={1}>
                <CircularProgress size={24} />
              </Stack>
            )}
            {loadMorePendingFailed && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={loadMorePendingSettlements}>
                    {t("Retry")}
                  </Button>
                }
              >
                {t("Failed to load more results")}
              </Alert>
            )}
          </Stack>
          </Box>
        )}
      </Box>

      <LowCounterValueConfirmModal
        opened={lowValueConfirmOpen}
        setOpened={setLowValueConfirmOpen}
        warnings={lowValueWarnings}
        onConfirm={handleConfirmLowValueBulkAdd}
      />

      <CustomModal
        opened={Boolean(editingCounterId)}
        setOpened={(value) => {
          if (!value) {
            setEditingCounterId(null);
          }
        }}
        title={t("Edit counter value")}
        width="min(480px, 92vw)"
        content={
          editingCounterId ? (
            <EquipmentCounterForm
              id={editingCounterId}
              hiddenFields={["equipment_id", "job", "kind"]}
              onSuccess={() => {
                setEditingCounterId(null);
                window.dispatchEvent(new Event("equipmentCounterAdded"));
              }}
            />
          ) : (
            <></>
          )
        }
      />
    </Box>
  );
};
